import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Stack,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Switch,
  FormControlLabel,
  OutlinedInput,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import StopIcon from "@mui/icons-material/Stop";

// --- Minimal helpers ---
const DEFAULT_MODEL = "gpt-5-mini";
const DEFAULT_TEMP = 1;

function norm(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}+/gu, "");
}
function detectTable(q) {
  const t = norm(q);
  // contracte / expira / CON-
  if (
    t.includes("contract") ||
    t.includes("expira") ||
    t.includes("expir") ||
    /\bcon[-_]/.test(t)
  )
    return "taskjira";
  // awb / curier / livrat / comenzi / in tranzit
  if (
    t.includes("awb") ||
    t.includes("curier") ||
    t.includes("livrat") ||
    t.includes("comenz") ||
    t.includes("tranzit")
  )
    return "vanzari";
  // branduri / genti -> taskjira
  const brands = [
    "louis vuitton",
    "chanel",
    "hermes",
    "hermès",
    "dior",
    "prada",
    "gucci",
    "celine",
    "saint laurent",
    "ysl",
    "balenciaga",
    "fendi",
    "burberry",
    "loewe",
    "miu miu",
    "bottega",
    "givenchy",
    "valentino",
    "louboutin",
  ];
  if (
    brands.some((b) => t.includes(b)) ||
    t.includes("genti") ||
    t.includes("geanta")
  )
    return "taskjira";
  return "taskjira";
}

// Predefined prompts shown in the center when the chat is empty
const PRESET_PROMPTS = [
  "Ce contracte îmi expiră azi?",
  "Ce produse am în tranzit?",
  "Ultimele 10 genți Louis Vuitton și media de preț de intrare",
  "AWB-uri lipsă în ultimele 7 zile",
  "Comenzi cu profit negativ săptămâna asta",
];

// Multi-chat storage keys
const CHATS_KEY = "luxura_ai_chats_v1";
function loadChats() {
  try {
    const raw = localStorage.getItem(CHATS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function saveChats(chats) {
  try {
    localStorage.setItem(CHATS_KEY, JSON.stringify(chats));
  } catch {}
}
function chatMessagesKey(id) {
  return `${LS_KEY}_${id}`;
}
function loadMessagesFor(id) {
  try {
    const raw = localStorage.getItem(chatMessagesKey(id));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function saveMessagesFor(id, msgs) {
  try {
    localStorage.setItem(chatMessagesKey(id), JSON.stringify(msgs));
  } catch {}
}

function exportToCSV(items, filename = "ai_results.csv") {
  try {
    if (!Array.isArray(items) || items.length === 0) return;
    const cols = Array.from(new Set(items.flatMap((obj) => Object.keys(obj))));
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replaceAll('"', '""');
      return `"${s}"`;
    };
    const header = cols.map(esc).join(",");
    const rows = items
      .map((row) => cols.map((c) => esc(row[c])).join(","))
      .join("\n");
    const csv = header + "\n" + rows;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("CSV export error:", e);
  }
}

const API_BASE = "https://server-9p4hx.kinsta.app"; // keep base; endpoints below include leading slash
const ASK_ENDPOINT = `${API_BASE}/api/ai/luxura-ai-taskjira/ask`;
const SQL_ASK_ENDPOINT = `${API_BASE}/api/ai/luxura-ai-taskjira/sql-ask`;
const LS_KEY = "luxura_ai_taskjira_chat_v1";

function MessageBubble({ role, children, onCopy }) {
  const isUser = role === "user";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.25,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          maxWidth: "85%",
          p: 1.25,
          bgcolor: isUser ? "primary.main" : "background.paper",
          color: isUser ? "primary.contrastText" : "text.primary",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {children}
          </Typography>
          {!isUser && (
            <Tooltip title="Copiază răspunsul">
              <IconButton size="small" onClick={onCopy}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

function ResultTable({ items, onExport }) {
  if (!items || !items.length) return null;
  const cols = Object.keys(items[0]).slice(0, 8);
  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Rezultate: {items.length}{" "}
          {items.length > 50 ? "(afișez primele 50)" : ""}
        </Typography>
        {onExport && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onExport(items)}
          >
            Exportă CSV
          </Button>
        )}
      </Box>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {cols.map((c) => (
              <TableCell key={c} sx={{ textTransform: "capitalize" }}>
                {c.replace(/_/g, " ")}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.slice(0, 50).map((row, idx) => (
            <TableRow key={idx} hover>
              {cols.map((c) => (
                <TableCell key={c}>{String(row[c] ?? "—")}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function LuxuraAI_Taskjira() {
  const [chats, setChats] = useState(() => {
    const list = loadChats();
    if (list.length) return list;
    const id = Date.now().toString();
    const initial = [{ id, title: "Chat 1", createdAt: Date.now() }];
    saveChats(initial);
    saveMessagesFor(id, []);
    return initial;
  });
  const [activeChatId, setActiveChatId] = useState(
    () => loadChats()[0]?.id || chats[0]?.id
  );
  const [messages, setMessages] = useState(() => loadMessagesFor(activeChatId));
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Mode: 'ai' (default) or 'sql'
  const [mode, setMode] = useState("ai");
  const scrollRef = useRef(null);

  // Speech: STT & TTS
  const [isListening, setIsListening] = useState(false);
  const [speakReplies, setSpeakReplies] = useState(true); // speak assistant by default
  const [isSpeaking, setIsSpeaking] = useState(false); // Track if TTS is currently active
  const recognitionRef = useRef(null);
  const synthesisSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const sttSupported =
    typeof window !== "undefined" &&
    ("webkitSpeechRecognition" in window || "SpeechRecognition" in window);

  // fixed model/temp for minimal UI
  const model = DEFAULT_MODEL;
  const temperature = DEFAULT_TEMP;

  useEffect(() => {
    saveMessagesFor(activeChatId, messages);
  }, [messages, activeChatId]);
  useEffect(() => {
    setMessages(loadMessagesFor(activeChatId));
  }, [activeChatId]);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // SpeechRecognition setup
  useEffect(() => {
    if (!sttSupported) return;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SpeechRecognition();
    rec.lang = "ro-RO";
    rec.interimResults = true;
    rec.continuous = false;

    rec.onresult = (e) => {
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; ++i) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else setInput((prev) => (prev ? prev + " " : "") + t);
      }
      if (finalText.trim()) {
        setInput((prev) => finalText.trim());
        // auto-send final result
        setTimeout(() => handleSend(finalText.trim()), 50);
      }
    };
    rec.onerror = () => {
      setIsListening(false);
    };
    rec.onend = () => {
      setIsListening(false);
    };
    recognitionRef.current = rec;
    return () => {
      try {
        rec.abort();
      } catch {}
    };
  }, [sttSupported]);

  // TTS helper
  const speakText = (text) => {
    if (!synthesisSupported || !speakReplies || !text) return;
    try {
      const u = new window.SpeechSynthesisUtterance(text);
      u.lang = "ro-RO";
      u.rate = 1.0;
      u.pitch = 1.0;
      u.volume = 1.0;
      u.onstart = () => setIsSpeaking(true);
      u.onend = () => setIsSpeaking(false);
      u.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {
      setIsSpeaking(false);
    }
  };

  // Track TTS state on user interaction (e.g. StopIcon)
  useEffect(() => {
    if (!synthesisSupported) return;
    const handleVoicesChanged = () => {
      // This event can be used to update state if needed.
    };
    window.speechSynthesis.addEventListener(
      "voiceschanged",
      handleVoicesChanged
    );
    // Listen to speechSynthesis events for external TTS changes
    const handleSpeechBoundary = () => setIsSpeaking(true);
    const handleSpeechEnd = () => setIsSpeaking(false);
    window.speechSynthesis.addEventListener(
      "speechstart",
      handleSpeechBoundary
    );
    window.speechSynthesis.addEventListener("speechend", handleSpeechEnd);
    return () => {
      window.speechSynthesis.removeEventListener(
        "voiceschanged",
        handleVoicesChanged
      );
      window.speechSynthesis.removeEventListener(
        "speechstart",
        handleSpeechBoundary
      );
      window.speechSynthesis.removeEventListener("speechend", handleSpeechEnd);
    };
  }, [synthesisSupported]);

  const toggleMic = () => {
    if (!sttSupported) return;
    const rec = recognitionRef.current;
    if (!rec) return;
    if (isListening) {
      try {
        rec.stop();
      } catch {}
      setIsListening(false);
    } else {
      try {
        rec.start();
        setIsListening(true);
      } catch {}
    }
  };

  const startStreaming = (q) => {
    try {
      const params = new URLSearchParams();
      params.set("question", q);
      if (model) params.set("model", model);
      params.set("table", detectTable(q));
      params.set("temperature", "1");
      const url =
        `${API_BASE}/api/ai/luxura-ai-taskjira/ask-stream?` + params.toString();
      const es = new EventSource(url);
      let assistantIndex = null;
      es.onopen = () => {
        setMessages((prev) => {
          assistantIndex = prev.length;
          return [...prev, { role: "assistant", content: "" }];
        });
      };
      es.onmessage = (ev) => {
        // default 'message' event carries text chunks
        const chunk = ev.data || "";
        setMessages((prev) => {
          const next = [...prev];
          if (assistantIndex == null) assistantIndex = next.length - 1;
          next[assistantIndex] = {
            ...next[assistantIndex],
            content: (next[assistantIndex].content || "") + chunk,
          };
          return next;
        });
      };
      es.addEventListener("items", (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          setMessages((prev) => {
            const next = [...prev];
            if (assistantIndex == null) assistantIndex = next.length - 1;
            next[assistantIndex] = {
              ...next[assistantIndex],
              data: payload.items,
            };
            return next;
          });
        } catch {}
      });
      es.addEventListener("done", () => {
        es.close();
        setLoading(false);
      });
      es.onerror = () => {
        es.close();
        setLoading(false);
        setError("Streaming indisponibil. Revin la răspuns normal.");
      };
      return es;
    } catch (e) {
      setError("Eroare streaming: " + (e.message || "necunoscută"));
      setLoading(false);
      return null;
    }
  };

  // Helper: fallback SQL ask
  const runSqlAsk = async (q) => {
    try {
      const rsp = await fetch(SQL_ASK_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, model, limit: 500 }),
      });
      if (!rsp.ok) {
        const txt = await rsp.text();
        throw new Error(`SQL-ASK HTTP ${rsp.status}: ${txt}`);
      }
      const data = await rsp.json();
      // Compose a short header
      const header = data.ok
        ? `SQL: găsite ${data.count} rânduri.`
        : "Nu am primit un răspuns SQL.";
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `${header}\n${data.sql || ""}`,
          data: Array.isArray(data.items) ? data.items : [],
          rationale: data.rationale || "",
        },
      ]);
      setTimeout(() => speakText(header), 0);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Mod SQL a eșuat: ${err.message}` },
      ]);
    }
  };

  const handleSend = async (presetText) => {
    const q = (presetText ?? input).trim();
    if (!q || loading) return;
    setError("");
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setInput("");
    setLoading(true);
    if (mode === "sql") {
      await runSqlAsk(q);
      setLoading(false);
      return;
    }
    const autoTable = detectTable(q);

    // --- Chat memory context: find last assistant message with data
    const lastAssistantWithData = [...next]
      .reverse()
      .find(
        (m) => m.role === "assistant" && Array.isArray(m.data) && m.data.length
      );
    const context_items = lastAssistantWithData
      ? lastAssistantWithData.data.slice(0, 50).map((x) => ({
          issue_id: x.issue_id,
          summary: x.summary,
          expira_contract: x.expira_contract,
          pret_de_vanzare_ron: x.pret_de_vanzare_ron,
        }))
      : [];

    try {
      const rsp = await fetch(ASK_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: q,
          model,
          table: autoTable,
          temperature: 1,
          chat_id: activeChatId,
          context_items,
        }),
      });
      if (!rsp.ok) {
        const t = await rsp.text();
        throw new Error(`HTTP ${rsp.status}: ${t}`);
      }
      const data = await rsp.json();

      // Formatăm răspunsul inteligent, suportând atât intent-urile structurate cât și answer text
      if (data.intent && data.items) {
        const header =
          data.intent === "contracts_expiring"
            ? `Am găsit ${data.count} contracte care expiră în intervalul cerut.`
            : data.intent === "products_in_transit"
              ? `Produse în tranzit găsite: ${data.count}.`
              : data.intent === "last_brand_bags"
                ? `Ultimele ${data.count} genți ${data.brand || ""}. Media preț intrare: ${
                    data.average_pret_intrare_ron != null
                      ? Number(data.average_pret_intrare_ron).toFixed(2) +
                        " RON"
                      : "n/a"
                  }`
                : `Rezultat (${data.intent})`;
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: header, data: data.items },
        ]);
        setTimeout(() => speakText(header), 0);
        // If intent returned but with count === 0 or empty items, fallback to SQL
        if (data.count === 0 || data.items.length === 0) {
          await runSqlAsk(q);
          return;
        }
      } else if (typeof data.answer === "string") {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.answer },
        ]);
        setTimeout(() => speakText(data.answer), 0);
      } else {
        // Fallback: try generic text-to-SQL d
        await runSqlAsk(q);
      }
    } catch (e) {
      setError(e.message || "Eroare la trimitere.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2.5,
        borderRadius: 3,
        bgcolor: "background.paper",
        backgroundImage:
          "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0))",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
          Luxura AI
          <Typography
            component="span"
            variant="caption"
            sx={{ ml: 1, color: "text.secondary" }}
          >
            ({mode === "ai" ? "mod AI" : "mod SQL liber"})
          </Typography>
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant={mode === "ai" ? "contained" : "outlined"}
            sx={{ borderRadius: 2 }}
            onClick={() => setMode("ai")}
          >
            AI
          </Button>
          <Button
            size="small"
            variant={mode === "sql" ? "contained" : "outlined"}
            sx={{ borderRadius: 2 }}
            onClick={() => setMode("sql")}
          >
            SQL
          </Button>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={activeChatId}
              onChange={(e) => setActiveChatId(e.target.value)}
            >
              {chats.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            sx={{ borderRadius: 2 }}
            onClick={() => {
              const id = Date.now().toString();
              const title = `Chat ${chats.length + 1}`;
              const next = [...chats, { id, title, createdAt: Date.now() }];
              setChats(next);
              saveChats(next);
              saveMessagesFor(id, []);
              setActiveChatId(id);
            }}
          >
            New
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={{ borderRadius: 2 }}
            onClick={() => {
              setMessages([]);
              saveMessagesFor(activeChatId, []);
            }}
          >
            Clear
          </Button>
        </Stack>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Box
        ref={scrollRef}
        sx={{
          height: 420,
          overflowY: "auto",
          p: 1,
          position: "relative",
          bgcolor: "background.default",
          borderRadius: 2,
          border: "1px solid",
          borderColor: mode === "sql" ? "warning.light" : "divider",
          boxShadow: mode === "sql" ? 2 : 0,
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ maxWidth: 820, mx: "auto", textAlign: "center" }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Alege un prompt pentru a începe
              </Typography>
              <Stack
                direction="row"
                spacing={1.25}
                sx={{
                  flexWrap: "wrap",
                  rowGap: 1.25,
                  justifyContent: "center",
                }}
              >
                {PRESET_PROMPTS.map((p, i) => (
                  <Paper
                    key={i}
                    elevation={1}
                    onClick={() => handleSend(p)}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all .15s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Typography variant="body2">{p}</Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
        {messages.map((m, idx) => (
          <Box key={idx}>
            <MessageBubble
              role={m.role}
              onCopy={() => navigator.clipboard.writeText(m.content || "")}
            >
              {m.content}
            </MessageBubble>
            {/* Tabel structurat pentru răspunsuri cu intent-uri */}
            {m.role === "assistant" &&
              Array.isArray(m.data) &&
              m.data.length > 0 && (
                <ResultTable
                  items={m.data}
                  onExport={(rows) =>
                    exportToCSV(rows, `ai_results_${activeChatId}.csv`)
                  }
                />
              )}
            {m.role === "assistant" && m.rationale && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  rationale
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  value={m.rationale}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mt: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          placeholder="Scrie întrebarea ta… (Enter trimite, Shift+Enter = linie nouă)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Tooltip
          title={
            isListening
              ? "Oprire microfon"
              : sttSupported
                ? "Vorbește"
                : "STT indisponibil"
          }
        >
          <span>
            <IconButton onClick={toggleMic} disabled={!sttSupported}>
              {isListening ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {synthesisSupported && isSpeaking && (
          <Tooltip title="Oprește citirea acum">
            <span>
              <IconButton
                onClick={() => {
                  window.speechSynthesis.cancel();
                  setIsSpeaking(false);
                }}
              >
                <StopIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            speakReplies
              ? "Oprește citirea răspunsului"
              : "Pornește citirea răspunsului"
          }
        >
          <span>
            <IconButton
              onClick={() => setSpeakReplies((v) => !v)}
              disabled={!synthesisSupported}
            >
              {speakReplies ? <VolumeUpIcon /> : <VolumeOffIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Trimite (Enter)">
          <span>
            <IconButton
              color="primary"
              onClick={() => handleSend()}
              disabled={loading || !input.trim()}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
}
